import React, { useState, useContext } from 'react'
import styled, { css, DefaultTheme, ThemeContext } from 'styled-components'

type StyleProps = {
  tipPosition?: 'left' | 'right' | 'top' | 'bottom'
}

const ARROW_SIZE = 8
const ARROW_BASE = 16
const GAP_FROM_TRIGGER = 5

const StyledFieldTooltip = styled.div<StyleProps & { $isVisible?: boolean }>`
  background: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius};
  min-width: 135px;
  box-shadow: ${({ theme }) => theme.boxShadow.lvlTwo};
  position: absolute;
  z-index: 10000;

  ${({ $isVisible }) =>
    $isVisible
      ? css`
          opacity: 1;
          visibility: visible;
        `
      : css`
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
        `}
  transition: opacity 0.15s ease-out, visibility 0.15s ease-out, transform 0.15s ease-out;

  ${({ tipPosition = 'left', theme }) => {
    let boxPositionCss = ''
    let arrowShapeAndPositionCss = ''
    switch (tipPosition) {
      case 'right':
        boxPositionCss = `top: 50%; right: calc(100% + ${GAP_FROM_TRIGGER}px); transform: translateY(-50%);`
        arrowShapeAndPositionCss = `clip-path: polygon(0% 0%, 100% 50%, 0% 100%); right: -${ARROW_SIZE}px; top: 50%; transform: translateY(-50%); width: ${ARROW_SIZE}px; height: ${ARROW_BASE}px;`
        break
      case 'top':
        boxPositionCss = `left: 50%; top: calc(100% + ${GAP_FROM_TRIGGER}px); transform: translateX(-50%);`
        arrowShapeAndPositionCss = `clip-path: polygon(0% 100%, 50% 0%, 100% 100%); top: -${ARROW_SIZE}px; left: 50%; transform: translateX(-50%); width: ${ARROW_BASE}px; height: ${ARROW_SIZE}px;`
        break
      case 'bottom':
        boxPositionCss = `left: 50%; bottom: calc(100% + ${GAP_FROM_TRIGGER}px); transform: translateX(-50%);`
        arrowShapeAndPositionCss = `clip-path: polygon(0% 0%, 50% 100%, 100% 0%); bottom: -${ARROW_SIZE}px; left: 50%; transform: translateX(-50%); width: ${ARROW_BASE}px; height: ${ARROW_SIZE}px;`
        break
      case 'left':
      default:
        boxPositionCss = `top: 50%; left: calc(100% + ${GAP_FROM_TRIGGER}px); transform: translateY(-50%);`
        arrowShapeAndPositionCss = `clip-path: polygon(100% 0%, 0% 50%, 100% 100%); left: -${ARROW_SIZE}px; top: 50%; transform: translateY(-50%); width: ${ARROW_SIZE}px; height: ${ARROW_BASE}px;`
        break
    }
    return css`
      ${boxPositionCss}
      &:after {
        content: '';
        position: absolute;
        background: ${theme.colors.black};
        ${arrowShapeAndPositionCss}
      }
    `
  }}
`

const TooltipTriggerContainer = styled.span<{ $marginLeft?: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: ${({ $marginLeft }) => $marginLeft || '0px'};
  cursor: help;
`

type Props = {
  children: React.ReactElement
  content: React.ReactNode
  className?: string
  tipPosition?: 'left' | 'right' | 'top' | 'bottom'
  marginLeft?: string
}

const FieldTooltip = ({ children, content, className, tipPosition = 'left', marginLeft }: Props) => {
  const [isVisibleState, setIsVisibleState] = useState(false)

  const show = () => setIsVisibleState(true)
  const hide = () => setIsVisibleState(false)

  return (
    <TooltipTriggerContainer
      $marginLeft={marginLeft}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      <StyledFieldTooltip tipPosition={tipPosition} className={className} $isVisible={isVisibleState}>
        {content}
      </StyledFieldTooltip>
    </TooltipTriggerContainer>
  )
}

export default FieldTooltip
